'use client';

import { type FC, useCallback, useRef, useState } from 'react';
import {
    Text,
    OtpInput,
    withDialog,
    Button,
    Fragment,
    LoadingOverlay,
    type OtpInputRef,
} from '@kidslabs/mario-nextjs-react-ui-core';
import { OtpElapsedTimeCount } from '../otp-elapsed-time-count';
import { useTranslation } from 'next-i18next';
import { observer } from '@legendapp/state/react';
import { useAuthProvider } from '../../providers';
import { ErrorCodesEnum } from '../../enums';
import { NotificationChannel, ValidatePhoneType } from '../../types';
import { authStore$, clearLoginSessionData, clearOtpSessionData } from '../../stores/auth.store';
import { useAuth } from '../../hooks';
import {
    getOtpAttemptData,
    incrementOtpAttemptCount,
    lockOtpAttempts,
    resetOtpAttemptData,
} from '../../utils/otp-attempt-tracker';
import type { OtpInputAction, OtpVerificationContentProps } from './otp-verification-form.types';
import styles from './otp-verification-form.module.css';

/** Maximum number of incorrect OTP attempts before lockout */
const MAX_OTP_ATTEMPTS = 5;

/**
 * Error codes that indicate wrong OTP input — retryable on the same screen.
 * Backend returns these when OTP value is incorrect but session is still valid.
 */
const RETRYABLE_OTP_ERROR_CODES = [
    'CHALLENGE_CONTINUE', // Cognito custom auth: wrong OTP, new session returned
    'CodeMismatchException', // Cognito standard: wrong OTP code
    'CODE_MISMATCH', // Legacy Magento: "The request code is not correct"
];

/**
 * Error codes that indicate OTP session is exhausted — user must wait for OTP expiry
 * and resend. These should lock the OTP input immediately (same as max attempts).
 * NEVER redirect to phone entry for these errors.
 */
const SESSION_EXHAUSTED_ERROR_CODES = [
    'SESSION_EXHAUSTED', // Cognito: no new session returned after wrong OTP
    'OTP_MAX_ATTEMPTS_EXCEEDED', // Backend: Cognito blocked auth after max attempts
    'NotAuthorizedException', // Cognito: session terminated (max attempts or expired)
];

/**
 * Maps server error codes to i18n translation keys.
 * Falls back to the raw server message if no mapping exists.
 */
const getOtpErrorTranslationKey = (errorCode: string): string | undefined => {
    if (RETRYABLE_OTP_ERROR_CODES.includes(errorCode)) {
        return 'error_otp_incorrect';
    }
    if (errorCode === 'ExpiredCodeException' || errorCode === 'CODE_EXPIRED') {
        return 'error_otp_expired';
    }
    return undefined;
};

const OtpVerificationContent: FC<OtpVerificationContentProps> = observer(() => {
    const { t } = useTranslation('auth');
    const validatePhoneType = authStore$.validatePhoneType.get();
    const { requestValidatePhone, confirmRequestedValidationPhone } = useAuthProvider();
    const { setCurrentStep } = useAuth();

    const [isSubmit, setIsSubmit] = useState(false);
    const [inlineError, setInlineError] = useState<string | null>(null);
    const [isResending, setIsResending] = useState(false);
    const otpInputRef = useRef<OtpInputRef>(null);

    // ─── OTP attempt counter and lock state ──────────────────────────────
    // Persisted via localStorage (not Legend-State IndexedDB) because these
    // fields are NOT in module-core's initial store state. Legend-State's
    // syncObservable cannot reliably track dynamically-added fields.
    //
    // localStorage is synchronous — no hydration race conditions.
    // Survives page reload — prevents users from bypassing lockout.
    // Auto-expires after 10 minutes to prevent permanent lockout.
    //
    // isOtpLocked is local state initialized from persisted data so it
    // triggers re-renders for disabled UI, while the counter is read
    // directly from localStorage on each attempt (always fresh).
    const [isOtpLocked, setIsOtpLocked] = useState(() => getOtpAttemptData().locked);

    const handleSuccess = useCallback(() => {
        resetOtpAttemptData();
        clearLoginSessionData();
        // Transition to success celebration view instead of closing immediately.
        // The AuthSuccessView will call onAuthSuccess after the animation completes.
        setCurrentStep('AuthSuccess');
    }, [setCurrentStep]);

    const handleOtpFilled = useCallback(
        (otpValue: string) => {
            // Block input if locked
            if (isOtpLocked) {
                return;
            }

            setIsSubmit(true);
            setInlineError(null);

            confirmRequestedValidationPhone(otpValue)
                .then(result => {
                    if (result.errorCode === ErrorCodesEnum.SUCCESS) {
                        if (validatePhoneType === ValidatePhoneType.Login) {
                            handleSuccess();
                        } else {
                            // Pass returnRoute to Register screen
                            setIsSubmit(false);
                            setCurrentStep('Register');
                        }
                    } else {
                        setIsSubmit(false);

                        // Session exhausted (Cognito blocked or no more retries) — lock OTP input
                        // User must wait for OTP to expire, then resend.
                        if (
                            SESSION_EXHAUSTED_ERROR_CODES.includes(result.errorCode) ||
                            result.errorCode === 'NO_SESSION'
                        ) {
                            lockOtpAttempts();
                            setIsOtpLocked(true);
                            setInlineError(
                                result.errorCode === 'NO_SESSION'
                                    ? t('error_otp_session_expired')
                                    : t('error_otp_max_attempts')
                            );
                        } else {
                            // Wrong OTP or other retryable error — increment attempt count
                            const currentAttempts = incrementOtpAttemptCount();

                            // Map error code to i18n message
                            const translationKey = getOtpErrorTranslationKey(result.errorCode);
                            const errorMessage = translationKey ? t(translationKey) : result.message;

                            if (currentAttempts >= MAX_OTP_ATTEMPTS) {
                                // Lock OTP input — user must wait for OTP to expire
                                lockOtpAttempts();
                                setIsOtpLocked(true);
                                setInlineError(t('error_otp_max_attempts'));
                            } else {
                                setInlineError(errorMessage);
                            }
                        }
                    }
                })
                .catch(error => {
                    setIsSubmit(false);
                    // Map Magento legacy errors to i18n
                    const errorMessage =
                        error.message === 'The request code is not correct' ? t('error_otp_incorrect') : error.message;

                    const currentAttempts = incrementOtpAttemptCount();

                    if (currentAttempts >= MAX_OTP_ATTEMPTS) {
                        lockOtpAttempts();
                        setIsOtpLocked(true);
                        setInlineError(t('error_otp_max_attempts'));
                    } else {
                        setInlineError(errorMessage);
                    }
                })
                .finally(() => {
                    setIsSubmit(false);
                });
        },
        [confirmRequestedValidationPhone, setCurrentStep, validatePhoneType, handleSuccess, isOtpLocked, t]
    );

    const onResend = useCallback(() => {
        setInlineError(null);
        setIsResending(true);

        // ─── Clear all stale OTP session data before resend ─────────────
        // This resets confirmResponse, currentOtp, and store-level fields
        // — ensuring a clean slate for the new OTP session.
        clearOtpSessionData();

        // Reset persisted attempt counter and unlock OTP input for the new session
        resetOtpAttemptData();
        setIsOtpLocked(false);

        // Clear OTP input values and restore focus to first cell
        otpInputRef.current?.clear();

        requestValidatePhone({ otpChannel: authStore$.notificationChannel.get() ?? NotificationChannel.Zalo })
            .then(result => {
                setIsResending(false);
                if (result.errorCode !== ErrorCodesEnum.SUCCESS) {
                    // Show resend errors inline — never navigate away from OTP screen.
                    // Rate limit errors (e.g. "Vui lòng chờ 88 giây...") stay inline.
                    setInlineError(result.message);
                }
            })
            .catch(error => {
                setIsResending(false);
                setInlineError(error.message);
            });
    }, [requestValidatePhone]);

    const handleOtpTimeout = useCallback(() => {
        // OTP expired — unlock input and allow resend
        setIsOtpLocked(false);
        setInlineError(null);
    }, []);

    const onOtpChangeActions = useCallback(
        (action: OtpInputAction, value: string) => {
            // Block input if locked
            if (isOtpLocked) {
                return;
            }

            // Clear inline error when user starts typing a new OTP
            if (action === 'change' || action === 'clear') {
                setInlineError(null);
            }

            if (action === 'filled' && value?.length === 6) {
                handleOtpFilled(value);
            }
        },
        [handleOtpFilled, isOtpLocked]
    );

    const backToChannelSelectorStep = useCallback(() => {
        setCurrentStep('SelectChannel');
    }, [setCurrentStep]);

    const isInputDisabled = isOtpLocked || isResending;

    return (
        <Fragment>
            <div className={styles.container}>
                <div className={styles.labelWrapper}>
                    <Text className={styles.label}>{t('OTP')}</Text>
                    <Text className={styles.markRequired}>(*)</Text>
                </div>
                <div className={styles.otpWrapper}>
                    <OtpInput
                        ref={otpInputRef}
                        className={styles.containerOtp}
                        cellsContainerClassName={styles.inputOtp}
                        colorRole={inlineError ? 'destructive' : 'primary'}
                        onAction={onOtpChangeActions}
                        disabled={isInputDisabled}
                    />
                    <OtpElapsedTimeCount
                        time={120}
                        onResend={onResend}
                        onTimeout={handleOtpTimeout}
                        isLocked={isOtpLocked}
                    />
                </div>

                {inlineError && (
                    <div className={styles.inlineErrorWrapper}>
                        <Text className={styles.inlineErrorText}>{inlineError}</Text>
                    </div>
                )}

                <Button onClick={backToChannelSelectorStep} className={styles.loginWithOrthersWayButton}>
                    <Text className={styles.secondaryText}>
                        {t('text_login_by_other_method', 'Thử đăng nhập bằng phương thức khác')}
                    </Text>
                </Button>
            </div>
            <LoadingOverlay
                showIf={isSubmit || isResending}
                className={styles.loadingOverlay}
                message={
                    isResending
                        ? t('text_resending_otp', 'Đang gửi lại mã OTP')
                        : t('text_logging_in', 'Đang đăng nhập')
                }
            />
        </Fragment>
    );
});

const OtpVerificationInner: FC<OtpVerificationContentProps> = props => {
    return <OtpVerificationContent {...props} />;
};

export const OtpVerification = withDialog(OtpVerificationInner);
